import type VectorLayer from 'ol/layer/Vector'
import type VectorSource from 'ol/source/Vector'

import { createNonBlockingRenderByTime } from '../../renderingQueue'
import { getLayerMetadata, setLayerMetadata } from '../layerMetadata'

import { findFeature } from '../findFeature'

import { updateGeofenceFeature } from './utils/updateGeofenceFeature'
import { findGeofenceMarker } from './utils/findGeofenceMarker'

const nonblockingRender = createNonBlockingRenderByTime()

export const updateGeofenceMapSelection = async (
  layer: VectorLayer<VectorSource>,
  mapMarkers: uui.domain.ui.map.markers.MapMarkers['geofence'],
  mapStyles: uui.domain.ui.map.markers.MapStyles['geofence'],
  selection: Set<string>,
) => {
  // retrieve selected items in the current layer
  const layerSelection = (getLayerMetadata(layer, 'selection') ?? new Set()) as Set<string>

  // if prev and next are empty do nothing
  if (selection.size !== 0 || layerSelection.size !== 0) {
    const geofencesToDeselect = new Set(layerSelection)

    nonblockingRender.reset()

    for (const markerId of selection) {
      await nonblockingRender.next()

      // remove it from geofenceId to be deselected
      geofencesToDeselect.delete(markerId)

      if (layerSelection.has(markerId)) {
        // geofenceId was previously selected and it's still selected
        // do nothing
        continue
      }

      const marker = findGeofenceMarker(mapMarkers, markerId)
      if (!marker) {
        throw new Error(
          `Trying to select Geofence marker with id: ${markerId} but no feature exist on the map`,
        )
      }

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        throw new Error(
          `Trying to select Geofence marker with id: ${markerId} but no feature exist on the map`,
        )
      }

      // the geofenceId is a new selection
      updateGeofenceFeature(mapStyles, marker, feature, true)
    }

    nonblockingRender.reset()

    for (const markerId of geofencesToDeselect) {
      await nonblockingRender.next()

      const marker = findGeofenceMarker(mapMarkers, markerId)
      if (!marker) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Geofence marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }
        continue
      }

      const feature = findFeature(layer, marker.id)

      if (!feature) {
        // fail silently in production when deselecting unknown items
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            `Trying to deselect Geofence marker with lat-lng: ${markerId} for ID: ${markerId} but no feature exist on the map`,
          )
        }

        continue
      }

      // deselect the geofence
      updateGeofenceFeature(mapStyles, marker, feature, false)
    }
  }

  // update the stored collection
  setLayerMetadata(layer, 'selection', selection)
}
